/**
 * A tradução `nome da tool` → `chave do UI_REGISTRY` ([T015] / US-4).
 *
 * Vive num arquivo próprio, sem import de registry nem de API, porque os dois
 * lados precisam dela e não podem se importar: `api/apiChat.js` (que resolve o handler quando
 * o `tool-input-available` chega com `executor: 'client'`) e
 * `ui-registry/plataforma.js` (que registra as entradas). Se cada um
 * escrevesse a própria regra, um `abrir_cliente` viraria
 * `plataforma/abrir_cliente` de um lado e `plataforma/abrir-cliente` do outro —
 * e o desfecho seria "ação desconhecida" com as duas suítes verdes.
 *
 * ★ Este arquivo NÃO pode importar o registry: `apiChat` está no grafo de
 *   `apiFeeFixoIa` (`extends APIChat`), e o parcial da plataforma puxa o
 *   catálogo de navegação inteiro. Foi exatamente esse tipo de ciclo que
 *   derrubou 8 suítes em 26/08 ([T010]). O único import é o slice
 *   `store/toolParts.js`, que é FOLHA (não importa nada) — não fecha ciclo.
 */
import { TOOL_PART_STATE } from '@modules/aawz-intelligence-v2/store/toolParts'

/** O módulo dono das tools de front e do cartão genérico delas. */
export const MODULO_DA_PLATAFORMA = 'plataforma'

/** O cartão que a ação oferecida (ou recusada) desenha no thread. */
export const CHAVE_ACAO_OFERECIDA = MODULO_DA_PLATAFORMA + '/acao-oferecida'

/**
 * Quem roda a ferramenta (`contracts/events.json` → `tool-input-available.executor`).
 * Mora aqui porque é o eixo que separa a tool de front de todas as outras — e
 * quem precisa dele é justamente quem não pode se importar: o parser, o
 * renderer e o registry parcial da plataforma.
 */
export const EXECUTOR_CLIENTE = 'client'

/**
 * @param {string} nome  Nome da tool como o backend a registra (`abrir_cliente`).
 * @returns {?string} `plataforma/abrir-cliente`, ou `null` sem nome.
 */
export function chaveDaToolDeFront(nome) {
  if (!nome || typeof nome !== 'string') return null
  return MODULO_DA_PLATAFORMA + '/' + nome.replace(/_/g, '-')
}

// ─── O `ui` da ação, nas DUAS vidas do cartão ([T024]) ──────────────────────
//
// ★ Por que a derivação mora AQUI, e não no parser nem na bolha.
//
// As quatro tools de front declaram `UI_SCHEMA = None` no backend: o `ui` delas
// não vem do servidor em vida nenhuma — quem o monta é o front. E o front o
// monta em DOIS momentos: no parser, quando o `tool-input-available` chega
// (turno em andamento), e no renderer, quando a conversa é reaberta e o que
// existe é `tool_calls[] = {id, name, input, executor: 'client', output}`, sem
// `ui` (`contracts/usecases.md` §US-4).
//
// Escrever a mesma montagem nos dois lugares é como o cartão ao vivo e o
// recarregado divergem — e a divergência apareceria como "a conversa reaberta
// mostra outro cartão", meses depois, sem nada vermelho. Este arquivo já era o
// lugar onde os dois lados combinam o vocabulário das tools de front, e segue
// SEM NENHUM import (o motivo está no cabeçalho).

/** Os estados do contrato (`events.json` → `ToolState`) que a ação usa. */
export const ESTADO_ACAO_OFERECIDA = TOOL_PART_STATE.ENTRADA
export const ESTADO_ACAO_FEITA = TOOL_PART_STATE.SAIDA
export const ESTADO_ACAO_RECUSADA = TOOL_PART_STATE.RECUSA
export const ESTADO_ACAO_COM_ERRO = TOOL_PART_STATE.ERRO

/**
 * O `ui` do cartão genérico da ação — a fonte única do shape.
 *
 * `namespace` viaja nos props porque é por ele que o clique reencontra a
 * instância de chat da superfície (`ui-registry/execucao.js`).
 *
 * ★ `bloqueado`/`href` (feedback do owner, 04/09): quando o `window.open`
 * automático foi barrado pelo navegador, o cartão precisa do `href` para
 * oferecer o botão "Abrir em nova aba" — um CLIQUE nele não é bloqueado, só a
 * navegação automática é. Sem o `href` aqui, o motivo aparece em texto e o
 * usuário não tem como recuperar a ação sem repetir o pedido ao modelo.
 */
export function uiDaAcaoDeFront({
  tool,
  label,
  input,
  motivo,
  namespace,
  estado,
  bloqueado,
  href,
}) {
  return {
    component: CHAVE_ACAO_OFERECIDA,
    state: estado || ESTADO_ACAO_OFERECIDA,
    props: {
      tool: tool || '',
      label: label || '',
      input: input || {},
      motivo: motivo || '',
      namespace: namespace || '',
      bloqueado: Boolean(bloqueado),
      href: href || '',
    },
  }
}

/**
 * Em que estado o cartão de uma chamada PERSISTIDA deve desenhar.
 *
 * O discriminante é o `output`, que é o que o banco guarda: ausente (ou `null`)
 * significa que o navegador ainda não respondeu — a chamada está PENDENTE, e é
 * ela que precisa reaparecer com botão quando a conversa é reaberta. Com
 * resposta, o desfecho está nela.
 */
export function estadoDaAcaoPeloOutput(output) {
  if (!output || typeof output !== 'object') return ESTADO_ACAO_OFERECIDA
  if (output.recusado) return ESTADO_ACAO_RECUSADA
  if (output.erro) return ESTADO_ACAO_COM_ERRO
  return ESTADO_ACAO_FEITA
}

/** O que o cartão explica quando a ação não aconteceu. */
export function motivoDaAcaoPeloOutput(output) {
  if (!output || typeof output !== 'object') return ''
  return String(output.motivo || output.erro || '')
}

/** O pop-up foi bloqueado pelo navegador nesta chamada persistida? */
export function bloqueadoDaAcaoPeloOutput(output) {
  return Boolean(output && typeof output === 'object' && output.bloqueado)
}

/** O `href` que o botão de fallback ("Abrir em nova aba") reabre. */
export function hrefDaAcaoPeloOutput(output) {
  if (!output || typeof output !== 'object') return ''
  return String(output.href || '')
}
